import SentryService from '@services/SentryService';

import type { HealthState } from './types';

/** Silence threshold — below this RMS level, audio is considered silent */
const SILENCE_THRESHOLD = 1;

/** Time before first warning (silence → degraded) */
const SOFT_WARNING_MS = 8000;

/** Time before failure state (silence → failed) */
const HARD_FAILURE_MS = 15000;

/** Grace period after initialization before raising alerts */
const INIT_GRACE_MS = 2000;

/** Analysis polling interval */
const POLL_INTERVAL_MS = 50;

/** AnalyserNode FFT size (matching existing WebRecordingService) */
const FFT_SIZE = 32;

/** Emit perf telemetry every Nth poll (5 = 250ms for launch baseline, 20 = 1s steady state) */
const PERF_SAMPLE_INTERVAL = 5;

type HealthChangeHandler = (state: HealthState, prev: HealthState) => void;
type LevelChangeHandler = (level: number) => void;

export interface PerfSnapshot {
  analyser_call_ms: number;
  audio_context_state?: string;
  polls_since_start: number;
  estimated_cpu_percent: number;
}

export interface PerformanceSummary {
  session_duration_seconds: number;
  total_polls: number;
  analyser_duration_avg_ms: number;
  analyser_duration_max_ms: number;
  state_transition_count: number;
  audio_context_state?: string;
  base_latency_ms?: number;
}

type PerfSampleHandler = (perf: PerfSnapshot) => void;

/**
 * Monitors audio health using WebAudio AnalyserNode RMS analysis.
 * Implements a state machine: Idle → Initializing → Healthy → Degraded → Failed.
 */
export class AudioHealthMonitor {
  private state: HealthState = 'idle';
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private pollTimerId: ReturnType<typeof setInterval> | null = null;
  private lastAudioTime = 0;
  private startTime = 0;
  private ownsAudioContext = false;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;

  // Performance counters
  private pollCount = 0;
  private maxAnalyzeDurationMs = 0;
  private totalAnalyzeDurationMs = 0;
  private stateTransitionCount = 0;

  private onHealthChanged: HealthChangeHandler | null = null;
  private onLevelChanged: LevelChangeHandler | null = null;
  private onPerfSample: PerfSampleHandler | null = null;

  /** Set the callback for health state changes */
  setOnHealthChanged(handler: HealthChangeHandler | null): void {
    this.onHealthChanged = handler;
  }

  /** Set the callback for audio level updates (called every poll interval, ~50ms) */
  setOnLevelChanged(handler: LevelChangeHandler | null): void {
    this.onLevelChanged = handler;
  }

  /** Set the callback for sampled performance snapshots (called every Nth poll) */
  setOnPerfSample(handler: PerfSampleHandler | null): void {
    this.onPerfSample = handler;
  }

  /** Get the current health state */
  getState(): HealthState {
    return this.state;
  }

  /** Get aggregated performance summary for the current monitoring session */
  getPerformanceSummary(): PerformanceSummary {
    return {
      session_duration_seconds: this.startTime ? (Date.now() - this.startTime) / 1000 : 0,
      total_polls: this.pollCount,
      analyser_duration_avg_ms:
        this.pollCount > 0 ? this.totalAnalyzeDurationMs / this.pollCount : 0,
      analyser_duration_max_ms: this.maxAnalyzeDurationMs,
      state_transition_count: this.stateTransitionCount,
      audio_context_state: this.audioContext?.state,
      base_latency_ms: this.audioContext
        ? ((this.audioContext as AudioContext & { baseLatency?: number }).baseLatency || 0) * 1000
        : undefined,
    };
  }

  /**
   * Start monitoring an audio stream.
   * Optionally accepts an external AudioContext to share with WebRecordingService.
   */
  start(stream: MediaStream, externalAudioContext?: AudioContext): void {
    this.stop();

    // Use external AudioContext or create our own
    if (externalAudioContext) {
      this.audioContext = externalAudioContext;
      this.ownsAudioContext = false;
    } else {
      this.audioContext = new AudioContext();
      this.ownsAudioContext = true;
    }

    // If AudioContext is suspended (common on iOS), don't raise false failures
    if (this.audioContext.state === 'suspended') {
      try {
        this.audioContext.resume();
      } catch {
        // Will stay in Initializing until AudioContext resumes
      }
    }

    try {
      // Set up AnalyserNode
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = FFT_SIZE;
      this.analyser.smoothingTimeConstant = 0; // Instant response
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

      // Connect stream to analyser
      this.sourceNode = this.audioContext.createMediaStreamSource(stream);
      this.sourceNode.connect(this.analyser);
    } catch (error) {
      this.stop(); // Clean up partially-initialized state
      throw error;
    }

    // Initialize timing
    this.startTime = Date.now();
    this.lastAudioTime = Date.now();

    // Reset performance counters
    this.pollCount = 0;
    this.maxAnalyzeDurationMs = 0;
    this.totalAnalyzeDurationMs = 0;
    this.stateTransitionCount = 0;

    // Transition to Initializing
    this.setState('initializing');

    // Start polling
    this.pollTimerId = setInterval(() => this.analyze(), POLL_INTERVAL_MS);
  }

  /** Stop monitoring and clean up */
  stop(): void {
    if (this.pollTimerId !== null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    }

    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // May already be disconnected
      }
      this.sourceNode = null;
    }

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // May already be disconnected
      }
      this.analyser = null;
    }

    if (this.ownsAudioContext && this.audioContext) {
      if (this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(() => {});
      }
    }

    this.audioContext = null;
    this.dataArray = null;

    if (this.state !== 'idle') {
      this.setState('idle');
    }
  }

  /** Analyze the current audio level and update state machine */
  private analyze(): void {
    if (!this.analyser || !this.dataArray) return;

    // If AudioContext is suspended, attempt to resume it so health monitoring
    // continues even after Chrome auto-suspends idle AudioContexts.
    // Without this, health state freezes (usually at 'healthy') and stale
    // streams pass the reuse check on next recording start.
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
      return; // Skip this poll — next interval will re-check after resume settles
    }

    const t0 = performance.now();

    this.analyser.getByteFrequencyData(this.dataArray);

    // Compute average level
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i];
    }
    const average = sum / this.dataArray.length;

    // Emit normalized level (0-100) for VU meter visualization
    if (this.onLevelChanged) {
      const normalizedLevel = Math.min(100, (average / 128) * 100);
      this.onLevelChanged(normalizedLevel);
    }

    const now = Date.now();
    const timeSinceStart = now - this.startTime;
    const isSilent = average <= SILENCE_THRESHOLD;

    if (!isSilent) {
      this.lastAudioTime = now;
    }

    const silenceDuration = now - this.lastAudioTime;

    // State machine transitions
    switch (this.state) {
      case 'initializing':
        // Within grace period — suppress alerts
        if (timeSinceStart < INIT_GRACE_MS) break;

        if (!isSilent) {
          this.setState('healthy');
        } else if (silenceDuration >= HARD_FAILURE_MS) {
          this.setState('failed');
        } else if (silenceDuration >= SOFT_WARNING_MS) {
          this.setState('degraded');
        }
        break;

      case 'healthy':
        if (silenceDuration >= SOFT_WARNING_MS) {
          this.setState('degraded');
        }
        break;

      case 'degraded':
        if (!isSilent) {
          this.setState('healthy');
        } else if (silenceDuration >= HARD_FAILURE_MS) {
          this.setState('failed');
        }
        break;

      case 'failed':
        if (!isSilent) {
          this.setState('healthy');
        }
        break;
    }

    // Performance measurement
    const dt = performance.now() - t0;
    this.pollCount++;
    this.totalAnalyzeDurationMs += dt;
    if (dt > this.maxAnalyzeDurationMs) this.maxAnalyzeDurationMs = dt;

    // Emit sampled perf snapshot every Nth poll
    if (this.onPerfSample && this.pollCount % PERF_SAMPLE_INTERVAL === 0) {
      this.onPerfSample({
        analyser_call_ms: dt,
        audio_context_state: this.audioContext?.state,
        polls_since_start: this.pollCount,
        estimated_cpu_percent: (dt / POLL_INTERVAL_MS) * 100,
      });
    }
  }

  private setState(newState: HealthState): void {
    if (this.state === newState) return;

    const prev = this.state;
    this.state = newState;
    this.stateTransitionCount++;

    // Log significant state transitions
    if (newState === 'degraded' || newState === 'failed') {
      SentryService.addBreadcrumb({
        category: 'audio.health',
        message: `Audio health: ${prev} → ${newState}`,
        level: newState === 'failed' ? 'error' : 'warning',
      });
    }

    if (this.onHealthChanged) {
      this.onHealthChanged(newState, prev);
    }
  }
}
